import { useContext, useState } from 'react'
import { observer } from 'mobx-react'
import { Icon } from '@components/Generic'
import { StoreContext } from '@store/Root.store'
import { cx } from '@/utils/cx'
import type { IncomeCandidate } from '@/types/gocardlessIncomeConfirmation'
import { format } from '@/methods/format'

interface IncomeCandidateCardProps {
  candidate: IncomeCandidate
  currency: string | null
  selected: boolean
  disabled: boolean
  onToggle: () => void
}

export const formatAmount = (amount: number, currency: string | null, locale: string): string => {
  try {
    return new Intl.NumberFormat(
      locale,
      currency
        ? { style: 'currency', currency }
        : { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    ).format(amount)
  } catch {
    // a locale or currency code Intl does not know
    return currency ? `${amount.toFixed(2)} ${currency}` : amount.toFixed(2)
  }
}

// Booking dates come as YYYY-MM-DD without a time zone; format them as UTC so the day never
// shifts for users west of Greenwich.
export const formatDate = (date: string, locale: string): string => {
  const parsed = new Date(`${date}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return date

  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(parsed)
  } catch {
    return date
  }
}

// Keys of the per-month sums are YYYY-MM.
export const formatMonth = (month: string, locale: string): string => {
  const parsed = new Date(`${month}-01T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return month

  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(parsed)
  } catch {
    return month
  }
}

const formatIban = (iban: string): string =>
  iban
    .replace(/\s+/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim()

export const IncomeCandidateCard = observer(
  ({ candidate, currency, selected, disabled, onToggle }: IncomeCandidateCardProps) => {
    const store = useContext(StoreContext)
    const { gocardlessIncomeConfirmationPage: trans } = store.TranslationsState.translations
    const { language } = store.TranslationsState

    const [expanded, setExpanded] = useState(false)

    const inputId = `income-candidate-${candidate.id}`
    const detailsId = `${inputId}-details`

    // payer name, else the IBAN, else "unknown payer"
    const iban = candidate.payerIban ? formatIban(candidate.payerIban) : null
    const title = candidate.payerName || iban || trans?.unknownPayer
    const showIban = !!candidate.payerName && !!iban

    const categoryLabel = trans?.categories?.[candidate.category] ?? candidate.category
    const categoryHint = format(trans?.categoryHint, { category: categoryLabel }) ?? categoryLabel
    const paymentsLabel =
      candidate.count === 1
        ? trans?.singlePayment
        : format(trans?.payments, { count: candidate.count })
    const period =
      candidate.firstDate === candidate.lastDate
        ? formatDate(candidate.firstDate, language)
        : `${formatDate(candidate.firstDate, language)} – ${formatDate(candidate.lastDate, language)}`

    const months = Object.keys(candidate.monthly).sort()
    const hasDetails = months.length > 0 || candidate.transactions.length > 0

    return (
      <div
        className={cx('gocardless-ic__candidate', selected && 'gocardless-ic__candidate--selected')}
        data-testid={inputId}
      >
        <label className="gocardless-ic__candidate-label" htmlFor={inputId}>
          <input
            id={inputId}
            type="checkbox"
            checked={selected}
            disabled={disabled}
            onChange={onToggle}
          />
          <span className="gocardless-ic__checkbox" aria-hidden="true">
            <Icon
              type="check"
              size="14px"
              color="var(--checkbox-check-color, #fff)"
              visible={selected}
            />
          </span>

          <span className="gocardless-ic__summary">
            <span className="gocardless-ic__payer">{title}</span>
            <span className="gocardless-ic__meta">
              {showIban && <span>{iban}</span>}
              <span>{categoryHint}</span>
            </span>
            <span className="gocardless-ic__meta">
              <span>{paymentsLabel}</span>
              <span>{period}</span>
            </span>
          </span>

          <span className="gocardless-ic__amount">
            <span className="gocardless-ic__amount-value">
              {formatAmount(candidate.typicalMonthly, currency, language)}
            </span>
            <span className="gocardless-ic__amount-label">{trans?.perMonth}</span>
          </span>
        </label>

        {hasDetails && (
          <div className="gocardless-ic__details">
            <button
              type="button"
              className="gocardless-ic__toggle"
              aria-expanded={expanded}
              aria-controls={detailsId}
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? trans?.hideDetails : trans?.showDetails}
              <Icon type={expanded ? 'caret-up' : 'caret-down'} size="16px" />
            </button>

            {expanded && (
              <div id={detailsId}>
                {months.length > 0 && (
                  <>
                    <span className="gocardless-ic__details-title">{trans?.monthlyOverview}</span>
                    <ul className="gocardless-ic__months">
                      {months.map((month) => (
                        <li
                          key={month}
                          className={cx(
                            'gocardless-ic__month',
                            candidate.monthly[month] === 0 && 'gocardless-ic__month--empty'
                          )}
                        >
                          <span className="gocardless-ic__month-label">
                            {formatMonth(month, language)}
                          </span>
                          <span className="gocardless-ic__month-amount">
                            {formatAmount(candidate.monthly[month], currency, language)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {candidate.transactions.length > 0 && (
                  <>
                    <span className="gocardless-ic__details-title">{trans?.latestPayments}</span>
                    <ul className="gocardless-ic__transactions">
                      {candidate.transactions.map((transaction, index) => (
                        <li
                          key={`${transaction.date}-${index}`}
                          className="gocardless-ic__transaction"
                        >
                          <span className="gocardless-ic__transaction-date">
                            {formatDate(transaction.date, language)}
                          </span>
                          <span className="gocardless-ic__transaction-remittance">
                            {transaction.remittance || trans?.noRemittance}
                          </span>
                          <span className="gocardless-ic__transaction-amount">
                            {formatAmount(transaction.amount, currency, language)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
)
